import * as ImagePicker from 'expo-image-picker';
import CloseCircleLargeIcon from '@assets/icons/close-circle-x-large.svg';
import CloseCircleSmallIcon from '@assets/icons/close-circle-x-small.svg';
import i18n from '@/utils/i18n';
import ImageLibraryIcon from '@assets/icons/image-library.svg';
import React, { useRef, useState } from 'react';
import TakePhotoCircleIcon from '@assets/icons/take-photo-circle.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import { CAMERA_FILTER, CAMERA_POSITION } from '@/utils/camera';
import { createStyles } from '@/contexts/theme-context';
import { DeletePhotoActionSheet } from './delete-photo-action-sheet';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import {
  useCameraDevice,
  Camera as VisionCamera,
} from '@/react-native-vision-camera';
import { useCameraReady } from '@/hooks/use-camera-ready';
import { usePermissionDeniedAlert } from '@/hooks/use-permission-denied-alert';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  addPhotos: (photos: string[]) => void;
  handleClose: () => void;
  isLoading?: boolean;
  onDeletePhoto: (index: number) => void;
  onDone: () => void;
  onOpenPreview: (index: number) => void;
  photos: string[];
}

const MAX_TOTAL_PHOTO_LIMIT = 5;

export const Camera: React.FC<Props> = ({
  addPhotos,
  handleClose,
  isLoading,
  photos,
  onDeletePhoto,
  onDone,
  onOpenPreview,
}) => {
  const [styles, theme] = useStyles();
  const showPermissionDeniedAlert = usePermissionDeniedAlert();
  const { bottom, top } = useSafeAreaInsets();
  const { showErrorToast } = useToasts();

  const device = useCameraDevice(CAMERA_POSITION, CAMERA_FILTER);
  const cameraRef = useRef<VisionCamera>(null);

  const { cameraReady, setCameraReady } = useCameraReady();
  const [isActionSheetVisible, setIsActionSheetVisible] = useState(false);
  const [photoToDeleteIndex, setPhotoToDeleteIndex] = useState<number | null>(
    null,
  );

  const enforcePhotoLimit = (): boolean => {
    if (photos.length >= MAX_TOTAL_PHOTO_LIMIT) {
      showErrorToast(
        i18n.t('camera.photo_limit_reached', {
          photoLimit: MAX_TOTAL_PHOTO_LIMIT,
        }),
      );
      return true;
    }
    return false;
  };

  const takePhoto = async () => {
    if (!cameraReady || !cameraRef.current || enforcePhotoLimit()) {
      return;
    }

    // Vision Camera returns a PhotoFile with a platform file path.
    const photo = await cameraRef.current.takePhoto();

    // Normalize to file:// URI for RN Image and downstream consumers.
    const normalizedUri = photo?.path
      ? photo.path.startsWith('file://')
        ? photo.path
        : `file://${photo.path}`
      : undefined;

    if (normalizedUri) {
      addPhotos([normalizedUri]);
    }
  };

  const pickImagesFromMediaLibrary = async () => {
    if (enforcePhotoLimit()) {
      return;
    }
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    switch (status) {
      case ImagePicker.PermissionStatus.GRANTED: {
        const result = await ImagePicker.launchImageLibraryAsync({
          allowsMultipleSelection: true,
          exif: false,
          mediaTypes: 'images',
          orderedSelection: true,
          quality: 0.5,
          selectionLimit: MAX_TOTAL_PHOTO_LIMIT - photos.length,
        });

        if (!result.canceled) {
          const selectedUris = result.assets.map((asset) => asset.uri);
          addPhotos(selectedUris);
        }
        break;
      }
      case ImagePicker.PermissionStatus.DENIED: {
        showPermissionDeniedAlert(i18n.t('camera.media_access_required'));
        break;
      }
    }
  };

  const disabled = photos.length === 0;

  return (
    <View
      style={[
        styles.container,
        { marginBottom: bottom },
        Platform.OS === 'android' ? { marginTop: top } : null,
      ]}
    >
      {isLoading ? (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator color={theme.colors.white} size="large" />
        </View>
      ) : null}
      <TouchableOpacity
        onPress={handleClose}
        style={styles.closeButton}
        testID="close-camera-button"
      >
        <CloseCircleLargeIcon fill={theme.colors.white} />
      </TouchableOpacity>
      {device ? (
        <VisionCamera
          device={device}
          enableZoomGesture
          isActive={!isLoading}
          onError={() => {
            setCameraReady(false);
          }}
          onInitialized={() => setCameraReady(true)}
          photo
          ref={cameraRef}
          style={styles.camera}
          testID="menu-camera"
        />
      ) : (
        // Placeholder View if camera device is not supported
        <View style={styles.camera} />
      )}
      {photos.length > 0 ? (
        <ScrollView
          contentContainerStyle={styles.photosContainer}
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.photosOverlay}
        >
          {photos.map((uri, index) => (
            <TouchableOpacity
              disabled={isLoading}
              key={index}
              onPress={() => onOpenPreview(index)}
              testID={`photo-thumbnail-${index}`}
            >
              <Image
                source={{ uri }}
                style={styles.photo}
                testID={`thumbnail-image-${index}`}
              />
              <TouchableOpacity
                disabled={isLoading}
                onPress={() => {
                  setPhotoToDeleteIndex(index);
                  setIsActionSheetVisible(true);
                }}
                style={styles.deleteImageButton}
                testID={`delete-photo-button-${index}`}
              >
                <CloseCircleSmallIcon fill={theme.colors.white} />
              </TouchableOpacity>
            </TouchableOpacity>
          ))}
        </ScrollView>
      ) : null}
      <View style={styles.actionItemsContainer}>
        <TouchableOpacity
          disabled={isLoading}
          onPress={pickImagesFromMediaLibrary}
          testID="media-library-button"
        >
          <ImageLibraryIcon />
        </TouchableOpacity>
        <TouchableOpacity
          disabled={isLoading}
          onPress={takePhoto}
          style={styles.cameraButton}
          testID="camera-button"
        >
          <TakePhotoCircleIcon />
        </TouchableOpacity>
        <TouchableOpacity
          disabled={disabled || isLoading}
          onPress={onDone}
          style={[disabled ? styles.disabled : null]}
        >
          <Text style={[TYPOGRAPHY.pSemiBold, styles.buttonText]}>
            {i18n.t('done')}
          </Text>
        </TouchableOpacity>
        {isActionSheetVisible && (
          <DeletePhotoActionSheet
            isVisible={isActionSheetVisible}
            onClose={() => setIsActionSheetVisible(false)}
            onDelete={() => {
              if (photoToDeleteIndex !== null) {
                onDeletePhoto(photoToDeleteIndex);
                setPhotoToDeleteIndex(null);
              }
              setIsActionSheetVisible(false);
            }}
          />
        )}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  actionItemsContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  buttonText: {
    color: theme.colors.white,
    fontSize: 18,
  },
  camera: {
    flex: 1,
  },
  cameraButton: {
    height: 70,
    width: 70,
  },
  closeButton: {
    left: 16,
    position: 'absolute',
    top: 16,
    zIndex: 1,
  },
  container: {
    flex: 1,
  },
  deleteImageButton: {
    backgroundColor: theme.colors.transparent,
    position: 'absolute',
    right: 4,
    top: 4,
  },
  disabled: {
    opacity: 0.5,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    justifyContent: 'center',
    zIndex: 1,
  },
  photo: {
    borderRadius: 8,
    height: 64,
    width: 64,
  },
  photosContainer: {
    alignItems: 'flex-end',
    gap: 8,
    paddingBottom: 8,
    paddingHorizontal: 16,
  },
  photosOverlay: {
    ...StyleSheet.absoluteFillObject,
    bottom: 80,
  },
}));
